import { useDvFlagAccess } from "./featureFlag/index.js";
import flagService from "./featureFlag/flagService.js";

import axios from "axios";
import API from "@/services/API.js";
import Vue from "vue";

/**
 * Hazard Reporting Count service for Vue.js 2
 * Provides hazard reporting count for navbar badge display
 * Mimics the Pinia store functionality from Nuxt 3
 */
class HazardReportingService {
  constructor() {
    // Make the service reactive using Vue.observable
    this.state = Vue.observable({
      /** @type {number|undefined} */
      count: undefined,
      /** @type {boolean} */
      isLoading: false,
      /** @type {boolean} */
      initialized: false,
    });

    /** @type {number} */
    this.REFRESH_INTERVAL_MS = 30000; // 30 seconds

    /** @type {ReturnType<typeof setInterval>|null} */
    this.interval = null;

    // Don't auto-start interval on initialization - wait for explicit start
  }

  // Getter methods that access the reactive state
  get count() {
    return this.state.count;
  }

  get isLoading() {
    return this.state.isLoading;
  }

  get initialized() {
    return this.state.initialized;
  }

  /**
   * Computed property for hazard count display
   * Returns undefined if count is 0 or undefined, otherwise returns count as string
   * @returns {string|undefined}
   */
  get hazardCount() {
    if (!this.state.count || this.state.count === 0) {
      return undefined;
    }
    return this.state.count.toString();
  }

  /**
   * Initializes the service and starts the interval
   * Should be called when the navbar needs to display hazard reporting count
   * @returns {Promise<void>}
   */
  async initialize() {
    if (
      !this.state.initialized &&
      useDvFlagAccess("beepiz-hazard-reporting-can:read")
    ) {
      this.state.initialized = true;
      Promise.resolve().then(() => {
        setTimeout(() => {
          this.startInterval();
        }, 500);
      });
    }
  }

  /**
   * Fetches hazard reporting count from the API
   * @returns {Promise<void>}
   */
  async fetchCount() {
    if (this.state.isLoading === true) {
      return; // Prevent concurrent calls
    }

    this.state.isLoading = true;

    try {
      const endpoint = `${API.managementBaseURL}${API.endpoints.hazard_reporting.new_count}`;

      const response = await axios.get(endpoint);

      const data = response.data.data || response.data;

      // Ensure we have a number
      this.state.count = typeof data === "number" ? data : parseInt(data) || 0;
    } catch (error) {
      console.warn("Failed to fetch hazard reporting count:", error);
      this.state.count = undefined;
    } finally {
      this.state.isLoading = false;
    }
  }

  /**
   * Starts the automatic refresh interval
   * Only works in browser environment
   */

  startInterval() {
    if (typeof window !== "undefined" && !this.interval) {
      // Initial fetch when starting interval
      this.fetchCount();

      // Set up periodic refresh
      this.interval = setInterval(() => {
        this.fetchCount();
      }, this.REFRESH_INTERVAL_MS);
    }
  }

  /**
   * Stops the automatic refresh interval
   */
  stopInterval() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  /**
   * Manually refresh the count
   * @returns {Promise<void>}
   */
  async refreshCount() {
    return this.fetchCount();
  }

  /**
   * Resets the service state and stops intervals
   */
  reset() {
    this.stopInterval();
    this.state.count = undefined;
    this.state.isLoading = false;
    this.state.initialized = false;
  }

  /**
   * Destroys the service and cleans up resources
   */
  destroy() {
    this.reset();
  }
}

// Create a singleton instance
const hazardReportingService = new HazardReportingService();

export default hazardReportingService;
